import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { crafts } from "@/data/crafts";
import { dbHelpers } from "@/lib/database";

const CraftDetail = ({ craft, onClose }) => {
  const [progress, setProgress] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);

  useEffect(() => {
    loadProgress(craft);
  }, [loadProgress, craft]);

  const loadProgress = useCallback(async (craft) => {
    const existing = await dbHelpers.getCraftProgress(craft.id);
    if (existing) {
      setCompletedSteps(existing.completed_steps || []);
      const currentProgress = (existing.completed_steps?.length / craft.steps.length) * 100 || 0;
      setProgress(currentProgress);
      setCompleted(existing.completed || false);
    }
  }, []);

  const toggleStep = async (stepIndex) => {
    const newCompleted = completedSteps.includes(stepIndex)
      ? completedSteps.filter(i => i !== stepIndex)
      : [...completedSteps, stepIndex];

    setCompletedSteps(newCompleted);

    const newProgress = (newCompleted.length / craft.steps.length) * 100;
    const isComplete = newCompleted.length === craft.steps.length;

    // Save progress
    await dbHelpers.saveCraftProgress({
      craft_id: craft.id,
      completed_steps: newCompleted,
      progress: newProgress,
      completed: isComplete,
      updated_at: new Date().toISOString()
    });

    // Award achievement if completed
    if (isComplete) {
      toast.success('🎉 Craft completed! You earned a Magic Egg!');
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });

      await dbHelpers.addAchievement({
        type: 'star',
        category: 'crafts',
        count: 1,
        created_at: new Date().toISOString()
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white border-2 border-border rounded-3xl shadow-hard-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b-2 border-border p-6 flex justify-between items-start z-10">
          <div>
            <h2 data-testid="craft-detail-title" className="font-fredoka text-3xl font-bold text-foreground mb-2">
              {craft.title}
            </h2>
            <div className="flex gap-3 text-sm">
              <span className="px-3 py-1 bg-accent/20 text-accent rounded-full font-nunito font-semibold">
                {craft.difficulty}
              </span>
              <span className="px-3 py-1 bg-secondary/20 text-secondary-foreground rounded-full font-nunito font-semibold">
                {craft.time}
              </span>
            </div>
          </div>
          <button
            data-testid="close-craft-detail-button"
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-foreground" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="flex justify-between items-center mb-2">
            <span className="font-nunito text-sm font-semibold text-foreground">Progress</span>
            <span className="font-fredoka font-bold text-foreground">{progress}%</span>
          </div>
          <div className="h-3 bg-muted rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              className="h-full bg-gradient-to-r from-primary to-accent"
            />
          </div>
          {completed && (
            <p className="text-center mt-2 font-patrick text-lg text-grass">
              ✨ Craft Completed! ✨
            </p>
          )}
        </div>

        {/* Image */}
        <div className="px-6 pt-6">
          <img
            src={craft.image}
            alt={craft.title}
            className="w-full h-64 object-cover rounded-2xl border-2 border-border"
          />
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <div>
            <p className="font-nunito text-lg text-muted-foreground mb-4">
              {craft.description}
            </p>
          </div>

          {/* Materials */}
          <div>
            <h3 className="font-fredoka text-xl font-semibold text-foreground mb-3">
              What You Need:
            </h3>
            <ul className="grid grid-cols-2 gap-2">
              {craft.materials.map((material, index) => (
                <li
                  key={index}
                  className="flex items-center gap-2 font-nunito text-foreground"
                >
                  <span className="w-2 h-2 bg-primary rounded-full" />
                  {material}
                </li>
              ))}
            </ul>
          </div>

          {/* Steps */}
          <div>
            <h3 className="font-fredoka text-xl font-semibold text-foreground mb-3">
              Steps:
            </h3>
            <div className="space-y-3">
              {craft.steps.map((step, index) => (
                <button
                  key={index}
                  data-testid={`craft-step-${index}`}
                  onClick={() => toggleStep(index)}
                  className="w-full flex items-start gap-3 p-3 bg-muted/30 hover:bg-muted rounded-xl transition-colors text-left"
                >
                  <div
                    className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${completedSteps.includes(index)
                      ? 'bg-grass border-grass'
                      : 'border-border bg-white'
                      }`}
                  >
                    {completedSteps.includes(index) && (
                      <Check className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <span className={`font-nunito ${completedSteps.includes(index) ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                    {step}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Secret Code */}
          {craft.secretCode && (
            <div className="bg-gradient-to-r from-magic/20 to-accent/20 rounded-2xl p-4 border-2 border-magic/30">
              <p className="font-patrick text-center text-lg text-foreground">
                🔐 Secret Code: <span className="font-fredoka font-bold">{craft.secretCode}</span>
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const CraftsPage = () => {
  const [selectedCraft, setSelectedCraft] = useState(null);
  const [craftProgress, setCraftProgress] = useState({});

  useEffect(() => {
    loadAllProgress();
  }, []);

  const loadAllProgress = async () => {
    const allProgress = await dbHelpers.getAllCraftProgress();
    const progressMap = {};
    allProgress.forEach(item => {
      progressMap[item.craft_id] = item;
    });
    setCraftProgress(progressMap);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy': return 'text-grass border-grass bg-grass/10';
      case 'Medium': return 'text-secondary border-secondary bg-secondary/10';
      case 'Hard': return 'text-primary border-primary bg-primary/10';
      default: return 'text-muted-foreground border-border bg-muted';
    }
  };

  return (
    <div className="min-h-screen px-6 py-8 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-12"
      >
        <h1 className="font-fredoka text-5xl md:text-6xl font-bold text-foreground mb-4">
          Glitter & Glue Crafts ✨
        </h1>
        <p className="font-nunito text-lg text-muted-foreground">
          Follow step-by-step instructions to create amazing crafts!
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
        {crafts.map((craft, index) => {
          const progress = craftProgress[craft.id];

          return (
            <motion.div
              key={craft.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              data-testid={`craft-card-${craft.id}`}
              onClick={() => setSelectedCraft(craft)}
              className="bg-white border-2 border-border rounded-3xl shadow-hard-lg hover:shadow-hard-xl hover:-translate-y-1 transition-all cursor-pointer overflow-hidden"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={craft.image}
                  alt={craft.title}
                  className="w-full h-full object-cover"
                />
                {progress?.completed && (
                  <div className="absolute top-3 right-3 bg-grass text-white rounded-full p-2 shadow-lg">
                    <Check className="w-5 h-5" />
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                  <span className="text-4xl">{craft.icon}</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-5">
                <h3 className="font-fredoka text-xl font-bold text-foreground mb-2">
                  {craft.title}
                </h3>
                <p className="font-nunito text-sm text-muted-foreground mb-4 line-clamp-2">
                  {craft.description}
                </p>

                <div className="flex gap-2 mb-4">
                  <span className={`text-xs px-2 py-1 rounded-full font-nunito font-semibold border ${getDifficultyColor(craft.difficulty)}`}>
                    {craft.difficulty}
                  </span>
                  <span className="text-xs px-2 py-1 rounded-full font-nunito font-semibold border border-accent text-accent bg-accent/10">
                    {craft.time}
                  </span>
                </div>

                {/* Progress */}
                {progress && (
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-nunito text-muted-foreground">Progress</span>
                      <span className="font-fredoka font-bold text-foreground">{progress.progress}%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-primary to-accent transition-all"
                        style={{ width: `${progress.progress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      <AnimatePresence>
        {selectedCraft && (
          <CraftDetail
            craft={selectedCraft}
            onClose={() => {
              setSelectedCraft(null);
              loadAllProgress();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CraftsPage;
